import { Injectable, QueryList } from '@angular/core';

import { LocalStorageService } from '../storage/local-storage.service';
import { UtilService } from '../util/util.service';
import { LogService } from '../log/log.service';
import { AuthUserInfo } from './auth-user-info.model';
import { EnvironmentService } from '../environment/environment.service';
import { AuthCryptoService } from './auth-crypro.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpRequestService } from '../http/http-request.service';
import { HttpRequestOptions } from '../http/http-request-options.model';
import { PsfyIdpResponseDto } from './model/psfy-idp-response.dto';
import { NameValue } from '../model';
import { UserDto } from 'src/app/users/shared/dto/user.dto';
import { TokenService } from './token.service';
import { environment } from 'src/environments/environment';
import { TenantAuthorityEnum } from './enum/tenant-authority.enum';

@Injectable()
export class AuthService {
  private _tenant: string;
  private _headers: Headers;
  private _domain: string = '';
  private _identityServerRoot: string;
  private _psfyIdpRoot: string;
  private _apiRoot: string;

  public constructor(
    private _localStore: LocalStorageService,
    private _utilService: UtilService,
    private _environmentService: EnvironmentService,
    private _authCryptoService: AuthCryptoService,
    private _httpRequestService: HttpRequestService,
    private _tokenService: TokenService,
    private _logService: LogService
  ) {
    this._tenant = '';
    this._domain = '';

    const envConfig = this._environmentService.getEnvironmentConfig();

    this._apiRoot = envConfig.WebApiUrl;
    this._identityServerRoot = envConfig.WeblinkAuthUrl;
    this._psfyIdpRoot = envConfig.PersonifyIdpDomain;

    this._domain = 'https://' + window.location.hostname;
  }

  public getAuthServerLoginUrl = (tenant: string): string => {
    let returnUrl: string = window.location.href.split('#')[0];
    let url: string =
      this._identityServerRoot +
      '/connect/authorize?client_id=WeblinkConnectBrowserApp&redirect_uri=' +
      returnUrl +
      '&prompt=login&response_type=code%20id_token%20token&scope=InternalWebApi%20PublicWebApi%20openid%20profile%20email&state=14521753416150.8104837641585618&nonce=14521753416150.9365715514868498';

    if (this._utilService.isNotNullEmptyOrWhitespace(tenant)) {
      //if there is no tenant then the identity server will try to infer one and it will be returned in the return url
      url = url + '&acr_values=tenant:' + tenant;
    }

    return url;
  };

  public async getPsfyIdpLoginUrl(): Promise<string> {
    let returnUri = window.location.href;
    if (window.location.href.indexOf('#') > -1) {
      window.location.href.split('#')[0];
    }

    let queryParams = new HttpParams()
      .set('client_id', 'mc_trade_public_client')
      .set('redirect_uri', returnUri)
      .set('prompt', 'login')
      .set('response_type', 'code') // id_token token')
      .set('scope', 'mc_trade_api openid profile')
      .set('state', this._authCryptoService.generateState())
      .set('nonce', this._authCryptoService.generateNonce())
      .set('code_challenge', await this._authCryptoService.generateCodeVerifier())
      .set('code_challenge_method', 'S256');

    const uri = `${this._psfyIdpRoot}/connect/authorize?${queryParams.toString()}`;

    return uri;
  }

  public sendUserToAuthServerLogin(tenant: string): void {
    let url: string = this.getAuthServerLoginUrl(tenant);

    window.location.href = url;
  }

  public sendUserToPsfyIdpLogin(): void {
    this.getPsfyIdpLoginUrl().then((url: string) => {
      window.location.href = url;
    });
  }

  public getAuthServerLogoutUrl(): string {
    let idTokenHint: string = '';
    let currentIdToken = this.getIdToken();
    if (this._utilService.isNotNullEmptyOrWhitespace(currentIdToken)) {
      idTokenHint = '&id_token_hint=' + currentIdToken;
    }

    let currentUri: string = this._utilService.toLowerTrim(this._domain);
    currentUri = this._utilService.replaceAll(currentUri, ':', '%3A', true);
    currentUri = this._utilService.replaceAll(currentUri, '/', '%2F', true);
    currentUri = this._utilService.replaceAll(currentUri, '.', '%2E', true);

    let maxLength = 2080; //browser max url length

    let logoutUrl = ''; //this._identityServerRoot + '/connect/endsession?post_logout_redirect_uri=' + currentUri;

    // if (this._localStore.getBoolean('use-psfy-idp')) {
    const issuer = this._tokenService.decodeJwt(this.getAccessToken())?.Payload['iss'];
    if (issuer) {
      logoutUrl = `${issuer}/connect/endsession?post_logout_redirect_uri=${currentUri}`;
    } else {
      logoutUrl = `${this._psfyIdpRoot}/connect/endsession?post_logout_redirect_uri=${currentUri}`;
    }
    // }
    if ((logoutUrl + idTokenHint).length <= maxLength) {
      logoutUrl = logoutUrl + idTokenHint;
    }

    return logoutUrl;
  }

  public sendUserToAuthServerLogout(): void {
    let url: string = this.getAuthServerLogoutUrl();
    this.clearTokensAndUserInfo();
    window.location.href = url;
  }

  public clearTokensAndUserInfo(): void {
    //remove everything from local storage
    this._localStore.removeItem('wl-api-access-token');
    this._localStore.removeItem('wl-api-access-token-expires');
    this._localStore.removeItem('wl-api-id-token');
    this._localStore.removeItem('wl-api-user-info');
    this._localStore.removeItem('wl-tenant');
    this._localStore.removeItem('wl-tenant-https-url');
    this._localStore.removeItem('wl-api-id-token-expires');
  }

  public currentUserIsAuthenticated(): boolean {
    //return true;
    let isAuthenticated: boolean = false;

    let tokenValue: string = this.getAccessToken();
    if (tokenValue != '') {
      //verify access token has not expired
      let accessTokenExpiresDate: Date = this.getAccessTokenExpires();
      let currentDate: Date = new Date();
      if (accessTokenExpiresDate > currentDate) {
        isAuthenticated = true;
      } else {
        this.sendUserToAuthServerLogout();
      }
      if (isAuthenticated == true) {
        //verify id token has not expired
        let idTokenExpiresDate: Date = this.getIdTokenExpires();
        if (idTokenExpiresDate < currentDate) {
          isAuthenticated = false;
        }
      }
    }

    return isAuthenticated;
  }

  public hasPkceCodeInUrl(): boolean {
    let result = false;

    if (window.location.search) {
      var query = window.location.search.replace('?', '');
      if (this._utilService.contains(query.toLowerCase(), 'code=')) {
        result = true;
      }
    }

    return result;
  }

  public exchangePkceCodeForTokens() {
    const params = new URLSearchParams(window.location.search);

    if (params.get('state')) {
      const returnedState = params.get('state');
      const storedState = this._authCryptoService.retreiveState();

      if (returnedState == storedState) {
        const code = params.get('code');

        const options = new HttpRequestOptions();
        options.Headers.push({ name: 'Content-Type', value: 'application/x-www-form-urlencoded' });

        let body = 'grant_type=authorization_code';
        body += `&code=${code}`;
        body += '&client_id=mc_trade_public_client';
        body += `&redirect_uri=${window.location.protocol}//${window.location.host}/unauthenticated`;
        body += `&code_verifier=${this._authCryptoService.retrieveCodeVerifier()}`;

        return this._httpRequestService.post(`${this._psfyIdpRoot}/connect/token`, body, options).then((response: PsfyIdpResponseDto) => {
          //console.log(response);
          return this.extractAndStoreTokensFromAuthCodeResponse(response);
        });
      }
    }
  }

  public getAccessToken(): string {
    return this._localStore.getString('wl-api-access-token') || '';
  }

  public setAccessToken(accessToken: string): void {
    this._localStore.setString('wl-api-access-token', accessToken);
  }

  public getIdToken(): string {
    return this._localStore.getString('wl-api-id-token') || '';
  }

  public setIdToken(idToken: string): void {
    this._localStore.setString('wl-api-id-token', idToken);
  }

  public getAccessTokenExpires(): Date {
    let dte: Date = new Date(1900, 1, 1);
    dte = this._localStore.getDate('wl-api-access-token-expires');

    return dte;
  }

  public setAccessTokenExpires(dte: Date): void {
    this._localStore.setDate('wl-api-access-token-expires', dte);
  }

  public getIdTokenExpires(): Date {
    let dte: Date = new Date(1900, 1, 1);
    dte = this._localStore.getDate('wl-api-id-token-expires');

    return dte;
  }

  public setIdTokenExpires(dte: Date): void {
    this._localStore.setDate('wl-api-id-token-expires', dte);
  }

  public getTenant(): string {
    return this._localStore.getString('wl-tenant') || '';
  }

  public setTenant(tenant: string): void {
    this._localStore.setString('wl-tenant', tenant);
  }

  public getTenantHttpsUrl(): string {
    return this._localStore.getString('wl-tenant-https-url') || '';
  }

  public setTenantHttpsUrl(url: string): void {
    this._localStore.setString('wl-tenant-https-url', url);
  }

  public hasAuthenticationTokensInUrl(): boolean {
    let result: boolean = false;
    if (window.location.hash) {
      var hash = window.location.hash.replace('#', '');
      if (this._utilService.startswith(hash.toLowerCase(), 'code=')) {
        result = true;
      }
    }
    return result;
  }

  public getUserInfo(): Promise<AuthUserInfo> {
    //only pull from local storage
    let userInfo = this._localStore.getObject('wl-api-user-info');
    return Promise.resolve(userInfo);
  }

  public setUserInfo(userInfo: AuthUserInfo, authority: TenantAuthorityEnum = TenantAuthorityEnum.Reconcile): boolean {
    if (!this._utilService.isNotNullOrUndefined(userInfo)) {
      this._logService.log('setUserInfo', 'setUserInfo() received a null/undefined payload; ignoring it.');
      return false;
    }

    if (authority === TenantAuthorityEnum.Reconcile) {
      const selected = this.getTenant();
      const responseTenant = userInfo.wli_tenant;

      if (!this._utilService.toLowerTrim(selected)) {
        this._logService.log('setUserInfo', 'setUserInfo() Reconcile called with no tenant currently selected; rejecting.');
        return false;
      }

      if (!this._utilService.toLowerTrim(responseTenant)) {
        this._logService.log('setUserInfo', 'setUserInfo() Reconcile received a response with no wli_tenant; rejecting.');
        return false;
      }

      if (this._utilService.toLowerTrim(selected) !== this._utilService.toLowerTrim(responseTenant)) {
        // `warn`, not `log`: this is the one rejection branch an operator must be able to see.
        // A tenant mismatch means the server answered for a tenant other than the one selected
        // — the exact condition this fix exists to detect — and `log` is suppressed unless the
        // `wl-verbose-logging` flag is set, which no environment sets by default. Reported as a
        // `minor` at the wave 1 security review: before the fix a mismatch threw loudly (if
        // pathologically); after it, it produced nothing at all. The other rejection branches
        // stay on `log` deliberately — an absent selection or an empty response tenant is a
        // caller-shape problem, not a cross-tenant signal worth unconditional noise.
        this._logService.warn(
          'setUserInfo',
          `setUserInfo() Reconcile mismatch: selected tenant is '${selected}' but response is for '${responseTenant}'; rejecting.`
        );
        return false;
      }

      this._localStore.setObject('wl-api-user-info', userInfo);
      if (this._utilService.toLowerTrim(userInfo.wli_tenant_https_url)) {
        this.setTenantHttpsUrl(userInfo.wli_tenant_https_url);
      }
      return true;
    }

    // Establish
    this._localStore.setObject('wl-api-user-info', userInfo);
    if (this._utilService.toLowerTrim(userInfo.wli_tenant)) {
      this.setTenant(userInfo.wli_tenant);
    } else {
      this._logService.log('setUserInfo', 'Establish called with an empty wli_tenant; leaving any existing wl-tenant untouched.');
    }
    if (this._utilService.toLowerTrim(userInfo.wli_tenant_https_url)) {
      this.setTenantHttpsUrl(userInfo.wli_tenant_https_url);
    }
    return true;
  }

  public getAuthServerUrlWithRedirect(redirectUrl: string) {
    return `${this._identityServerRoot}/connect/authorize?client_id=WeblinkConnectBrowserApp&redirect_uri=${redirectUrl}&response_type=code%20id_token%20token&scope=InternalWebApi%20PublicWebApi%20openid&state=1727&nonce=2232`;
  }

  public getPortalUrlWithAccessTokenHash(routePath: string) {
    if (routePath[0] !== '/') {
      routePath = '/' + routePath;
    }

    let trimmedHttpsUrl = this.getTenantHttpsUrl();
    if (trimmedHttpsUrl[trimmedHttpsUrl.length - 1] === '/') {
      trimmedHttpsUrl = trimmedHttpsUrl.substring(0, trimmedHttpsUrl.length - 1);
    }
    return `${trimmedHttpsUrl}${routePath}#access_token=${this.getAccessToken()}`;
  }

  public setUserInfoForOktaUserWithTenantSelected(tenantId: string): Promise<boolean> {
    const apiOptions = new HttpRequestOptions();
    apiOptions.Headers.push(new NameValue('Content-Type', 'application/json'));
    apiOptions.Headers.push(new NameValue('Authorization', 'Bearer ' + this.getAccessToken()));
    apiOptions.Headers.push(new NameValue('Cache-Control', 'no-cache'));
    apiOptions.Headers.push(new NameValue('Pragma', 'no-cache'));
    apiOptions.Headers.push(new NameValue('X-Tenant', tenantId));

    const token = this._tokenService.decodeJwt(this.getAccessToken());

    const userRoute = `${this._apiRoot}/api/user/v2/${token.Payload['sub'].toString()}/auth-user-info`;
    return this._httpRequestService.get(userRoute, apiOptions).then((userInfo: AuthUserInfo) => {
      return this.setUserInfo(userInfo, TenantAuthorityEnum.Reconcile);
    });
  }

  public isUserPsfyStaff(): boolean {
    let decoded = this._tokenService.decodeJwt(this._localStore.getString('wl-api-access-token'));
    return (decoded?.Payload as any)?.['idp']?.toLowerCase() === 'okta';
  }

  private extractAndStoreTokensFromAuthCodeResponse(response: PsfyIdpResponseDto) {
    this.setAccessToken(response.access_token);
    this.setIdToken(response.id_token);

    let expiresDate = new Date();
    expiresDate.setMinutes(expiresDate.getMinutes() + response.expires_in / 60);
    this.setAccessTokenExpires(expiresDate);
    this.setIdTokenExpires(expiresDate);
    return this.getUserInfoFromPsfyIdp();
  }

  private getUserInfoFromPsfyIdp() {
    let url: string = this._psfyIdpRoot + '/connect/userinfo';
    let options = new HttpRequestOptions();
    options.Headers.push(new NameValue('Content-Type', 'application/x-www-form-urlencoded'));
    options.Headers.push(new NameValue('Authorization', 'Bearer ' + this.getAccessToken()));
    options.Headers.push(new NameValue('Cache-Control', 'no-cache'));
    options.Headers.push(new NameValue('Pragma', 'no-cache'));

    let apiOptions = new HttpRequestOptions();
    apiOptions.Headers.push(new NameValue('Content-Type', 'application/json'));
    apiOptions.Headers.push(new NameValue('Authorization', 'Bearer ' + this.getAccessToken()));
    apiOptions.Headers.push(new NameValue('Cache-Control', 'no-cache'));
    apiOptions.Headers.push(new NameValue('Pragma', 'no-cache'));

    return new Promise((resolve, reject) => {
      try {
        this._httpRequestService.get(url, options).then(
          (data: AuthUserInfo) => {
            let accessToken = this._tokenService.decodeJwt(this.getAccessToken());
            if (data.sub !== null && accessToken.Payload['idp']?.toLowerCase() === 'okta') {
              const userRoute = `${this._apiRoot}/api/user/v2/psfyidpuser/${data.sub}/auth-user-info/personify-user`;
              this.setTenant('Personify');
              apiOptions.Headers.push(new NameValue('X-Tenant', 'Personify'));
              return this._httpRequestService
                .get(userRoute, apiOptions)
                .then((userInfo: AuthUserInfo) => {
                  this.setUserInfo(userInfo, TenantAuthorityEnum.Establish);
                  resolve(true);
                })
                .catch((error) => {
                  this.clearTokensAndUserInfo();
                  reject(error);
                });
            } else {
              const tenantRoute = `${this._apiRoot}/api/user/v2/app-man-user/${data.sub}/by-psfy-idp-id`;
              return this._httpRequestService.get(tenantRoute, apiOptions).then((tenant) => {
                // we have to do this in two stages becuase we need to know the tenant to get the user info due to connection strings
                // and dependency injection on the API side.
                this.setTenant(tenant);
                apiOptions.Headers.push(new NameValue('X-Tenant', tenant));

                const userRoute = `${this._apiRoot}/api/user/v2/${data.sub}/auth-user-info`;
                return this._httpRequestService.get(userRoute, apiOptions).then((userInfo: AuthUserInfo) => {
                  this.setUserInfo(userInfo, TenantAuthorityEnum.Establish);
                  resolve(true);
                });
              });
            }
          },
          (error: any) => {
            this;
            reject(error);
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }
}
